import { DEFAULT_LANGUAGE } from '@dealstack/frontend/shared/mechanism/i18n/i18nLayer';
import { Languages } from '@dealstack/shared/lib/common/i18n/i18n';
import { DevTools, Tolgee } from '@tolgee/web';
import { setTolgee } from './tolgeeInstance';
import { withTolgee } from '@tolgee/i18next';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Fetch from 'i18next-fetch-backend';
import { initReactI18next } from 'react-i18next';
import intervalPlural from 'i18next-intervalplural-postprocessor';
import { dealstackAiIcu } from './dealstackAiIcu';

export interface SetupI18NOptions {
	commonApiUrl: string;
	tolgeeApiUrl?: string;
	tolgeeApiKey?: string;
	languageList: string[] | Languages[];
}

//https://github.com/i18next/react-i18next/issues/1587
declare module 'i18next' {
	interface CustomTypeOptions {
		returnNull: false;
	}
}

export const setupI18nAsync = async ({
	commonApiUrl,
	tolgeeApiUrl,
	tolgeeApiKey,
	languageList,
}: SetupI18NOptions): Promise<typeof i18n> => {
	// Tolgee wraps the existing i18next instance and provides the in-context
	// (in-page) editor that replaced Phrase.
	//
	// PRODUCTION: no API key is shipped (tolgeeApiKey is empty). Tolgee runs in
	// production mode and delegates loading to the i18next-fetch-backend below
	// (our backend → S3); in-context editing is enabled per-editor via the Tolgee
	// Tools browser extension, so nothing secret is in the bundle.
	//
	// NON-PROD (qa/review only): a narrowly-scoped tolgeeApiKey may be injected
	// (review.yaml → BE_TOLGEE_API_KEY). When present, Tolgee runs in dev mode and
	// the in-context editor is active directly, no extension required.
	const tolgee = Tolgee()
		.use(DevTools())
		.init({
			apiUrl: tolgeeApiUrl,
			apiKey: tolgeeApiKey || undefined,
			defaultLanguage: DEFAULT_LANGUAGE,
			fallbackLanguage: DEFAULT_LANGUAGE,
		});

	setTolgee(tolgee);
	i18n.use(dealstackAiIcu);

	await withTolgee(i18n, tolgee)
		.use(Fetch)
		.use(intervalPlural)
		.use(LanguageDetector)
		.use(initReactI18next)
		.init({
			// Runs before Tolgee's own processor, which withTolgee appends; it needs the raw resolved copy.
			postProcess: [dealstackAiIcu.name],
			postProcessPassResolved: true,
			backend: {
				loadPath: `${commonApiUrl}/translations/{{lng}}/{{ns}}`,
			},
			// i18next-fetch-backend marks only 5xx as retryable, and until a namespace
			// settles `useSuspense` keeps the whole tree suspended — so a persistent 5xx
			// (a gateway 502/503, say; the backend itself no longer emits one) shows a
			// blank page rather than raw keys. The default of 5 retries with doubling
			// backoff also means one failing namespace fans out into dozens of requests
			// against a backend that is already unwell. Bounded so it settles quickly and
			// the app renders, untranslated, instead of not rendering at all.
			maxRetries: 2,
			cleanCode: true,
			fallbackLng: DEFAULT_LANGUAGE,
			supportedLngs: languageList,
			interpolation: {
				escapeValue: false,
			},
			react: {
				useSuspense: true,
			},
			debug: false,
			returnNull: false,
			detection: {
				order: ['localStorage', 'navigator'],
			},
		});

	return i18n;
};
